import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { authAPI } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { CreditCard, CheckCircle, Warning } from "@phosphor-icons/react";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await authAPI.getResetToken(token);
        setTokenInfo(data);
      } catch (e) {
        setError(e.response?.data?.detail || "Reset link not found or already used");
      }
      setLoading(false);
    };
    load();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      await authAPI.resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate("/login"), 1500);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to reset password");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
        <p className="text-sm text-zinc-500">Loading...</p>
      </div>
    );
  }

  if (!tokenInfo) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4" data-testid="reset-password-error">
        <div className="max-w-sm w-full bg-white border border-zinc-200 rounded-sm p-8 text-center">
          <Warning size={36} weight="duotone" className="text-amber-500 mx-auto" />
          <h1 className="text-lg font-bold mt-3 text-zinc-900">Link Unavailable</h1>
          <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{error}</p>
          <div className="mt-6 flex gap-2 justify-center">
            <Button onClick={() => navigate("/forgot-password")} variant="outline" className="rounded-sm text-xs" data-testid="request-new-link-btn">
              Request a new link
            </Button>
            <Button onClick={() => navigate("/login")} variant="ghost" className="rounded-sm text-xs" data-testid="back-to-login-btn">
              Sign in
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4" data-testid="reset-password-done">
        <div className="max-w-sm w-full bg-white border border-zinc-200 rounded-sm p-8 text-center">
          <CheckCircle size={40} weight="duotone" className="text-emerald-500 mx-auto" />
          <h1 className="text-lg font-bold mt-3 text-zinc-900">Password updated</h1>
          <p className="text-sm text-zinc-600 mt-2">Redirecting you to sign in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="max-w-sm w-full bg-white border border-zinc-200 rounded-sm p-8">
        <div className="flex items-center gap-2 mb-1">
          <CreditCard size={20} weight="duotone" className="text-[#002FA7]" />
          <h1 className="text-xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: "DM Sans, sans-serif" }}>Expenser</h1>
        </div>
        <p className="text-[11px] uppercase tracking-[0.15em] text-zinc-400 font-bold mb-6">Set a new password</p>

        <p className="text-sm text-zinc-600 mb-4">
          Resetting password for <span className="font-medium text-zinc-800">{tokenInfo.email_masked}</span>
        </p>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">New password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters"
              className="mt-1 rounded-sm" data-testid="reset-password-input" autoFocus required />
          </div>
          <div>
            <Label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">Confirm password</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="mt-1 rounded-sm" data-testid="reset-password-confirm" required />
          </div>
          {error && <p className="text-xs text-red-500" data-testid="reset-password-error-msg">{error}</p>}
          <Button type="submit" disabled={submitting} data-testid="reset-password-submit"
            className="w-full bg-[#002FA7] hover:bg-[#002280] text-white rounded-sm">
            {submitting ? "Updating..." : "Update password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
